import React, { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/dist/client/router';
import Cookies from 'js-cookie';

import { isLocale } from '@/utils/i18n/isLocale';

import configs from '@/configs.json';

interface ContextProps {
	readonly locale: string;
	readonly defaultLocale: string;
	readonly __t: (key: string, fallback: string) => string;
}

export const LocaleContext = React.createContext<ContextProps>({
	locale: configs.staticLang,
	defaultLocale: configs.staticLang,
	__t: (_key, fallback) =>
		!configs?.isMultilang && configs?.strings?.[configs.staticLang]?.[_key]
			? configs?.strings?.[configs.staticLang]?.[_key]
			: fallback,
});

export const LocaleProvider: FC<{
	lang: string;
	defaultLang: string;
	langs: Array<string>;
	strings: {};
}> = ({ lang, defaultLang, langs, strings, children }) => {
	const defaultLocale = useRef(defaultLang);
	const locales = useRef(langs);
	const translationStrings = useRef(strings);
	const [locale, setLocale] = useState(lang);
	const { query } = useRouter();

	useEffect(() => {
		if (locale) {
			// store lang preference
			Cookies.set('supt_preferred_locale', locale, { expires: 365 });
			// update html lang attribute
			document.documentElement.lang = locale;
		}
	}, [locale]);

	// sync locale value on client-side route changes
	useEffect(() => {
		if (
			typeof query.lang === 'string' &&
			isLocale(query.lang, locales.current) &&
			locale !== query.lang
		) {
			setLocale(query.lang);
		}
	}, [query.lang, locale]);

	const __t = (key, fallback) => {
		return translationStrings.current?.[locale][key] ?? fallback;
	};

	return (
		<LocaleContext.Provider
			value={{
				locale,
				defaultLocale: defaultLocale.current,
				__t,
			}}
		>
			{children}
		</LocaleContext.Provider>
	);
};
